import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@ptg-shared/components/base.component';
import { BannerType } from '@ptg-shared/controls/banner/types/banner.model';
import { showBanner } from '@ptg-shared/utils/common.util';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UpdateColaInfoService } from '../../services/index';

export namespace UpdateColaInfoPopup {
  export interface Input {
    benefitId: string;
    annualCola: number;
    colaEligibleDate: string;
  }
}

@Component({
  selector: 'ptg-update-cola-info',
  templateUrl: './update-cola-info.component.html',
  styleUrls: ['./update-cola-info.component.scss']
})
export class UpdateColaInfoComponent extends BaseComponent implements OnInit, OnDestroy {
  bannerType = BannerType.Hidden;
  message = '';

  formSubmit$ = new Subject<void>();
  isSaving = false;

  form: FormGroup;
  minDate: Date;

  constructor(
    private dialogRef: MatDialogRef<UpdateColaInfoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UpdateColaInfoPopup.Input,
    private formBuilder: FormBuilder,
    @Inject(UpdateColaInfoService) private updateColaInfoService: UpdateColaInfoService
  ) {
    super();
    this.minDate = new Date();
    this.form = this.formBuilder.group({
      annualCola: [this.data.annualCola || 0, [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      colaEligibleDate: [this.data.colaEligibleDate || null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.checkFormSubmit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.formSubmit$.complete();
  }

  private checkFormSubmit(): void {
    this.formSubmit$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.form.invalid) {
          showBanner.call(this, BannerType.Fail, 'Form Validation', '', {
            customMessage: 'Please fill out all required fields correctly.'
          });
          return;
        }
        this.onSave();
      });
  }

  private onSave(): void {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    let colaEligibleDate: string | null = null;
    const selectedDate = this.form.value.colaEligibleDate;
    
    if (selectedDate) {
      try {
        // Parse the date regardless of format and convert to YYYY-MM-DD
        const dateObj = new Date(selectedDate);
        
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          colaEligibleDate = `${year}-${month}-${day}`;
        }
      } catch (error) {
        console.error('Error parsing date:', error);
      }
    }

    const payload = {
      colaAnnual: parseFloat(this.form.value.annualCola),
      colaEligibleDate: colaEligibleDate
    };

    this.updateColaInfoService.updateColaInfo(this.data.benefitId, payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          showBanner.call(this, BannerType.Success, 'COLA Information', 'updated');
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.isSaving = false;
          showBanner.call(this, BannerType.Fail, 'COLA Information', 'updating', {
            customMessage: error?.error?.message || 'An error occurred while updating COLA information.'
          });
        }
      });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    this.formSubmit$.next();
  }
}
